import React, { useEffect } from 'react';
import { Box, Typography, Container, Card, CardContent, Button, Grid } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { BLOG_POSTS } from '../data/blogPosts';

function useSEO(title: string, description: string, keywords?: string[]) {
  useEffect(() => {
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.setAttribute('content', title);
    let ogDesc = document.querySelector('meta[property="og:description"]');
    if (!ogDesc) {
      ogDesc = document.createElement('meta');
      ogDesc.setAttribute('property', 'og:description');
      document.head.appendChild(ogDesc);
    }
    ogDesc.setAttribute('content', description);
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (keywords?.length) {
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.setAttribute('name', 'keywords');
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.setAttribute('content', keywords.join(', '));
    }
    return () => {
      document.title = 'Reviving India';
    };
  }, [title, description, keywords]);
}

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? BLOG_POSTS.find((p) => p.slug === slug) : null;
  const slugNotFound = slug && !post;

  useSEO(
    post ? post.metaTitle : 'Blog | Best AI Course in Patna - Reviving India',
    post ? post.metaDescription : 'Discover the best AI course in Patna. AI for Everyone, Generative AI, and AI Teacher programs. Insights and updates from Reviving India.',
    post?.keywords,
  );

  if (slugNotFound) {
    return (
      <Box sx={{ minHeight: '100vh', pt: 10, pb: 8, bgcolor: '#F8FAFC', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2 }}>
        <Typography sx={{ fontSize: '1.25rem', color: '#64748B' }}>Blog post not found.</Typography>
        <Button onClick={() => navigate('/blog')} variant="contained" startIcon={<ArrowLeft size={18} />} sx={{ fontWeight: 600 }}>
          Back to Blog
        </Button>
      </Box>
    );
  }

  if (post) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          pt: 10,
          pb: 8,
          bgcolor: '#F8FAFC',
        }}
      >
        <Container maxWidth="md">
          <Box>
            <Button
              startIcon={<ArrowLeft size={18} />}
              onClick={() => navigate('/blog')}
              sx={{
                color: '#2563EB',
                fontWeight: 600,
                textTransform: 'none',
                mb: 3,
                '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.08)' },
              }}
            >
              Back to Blog
            </Button>

            <Box
              component="article"
              sx={{
                bgcolor: '#fff',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                border: '1px solid #E5E7EB',
              }}
            >
              <Box
                component="img"
                src={post.image}
                alt={post.title}
                loading="eager"
                sx={{
                  width: '100%',
                  height: { xs: 220, md: 320 },
                  objectFit: 'cover',
                  objectPosition: 'top',
                  display: 'block',
                  imageRendering: 'auto',
                  transform: 'translateZ(0)',
                  backfaceVisibility: 'hidden',
                }}
              />
              <Box sx={{ p: { xs: 3, md: 5 } }}>
                <Typography
                  sx={{
                    fontSize: '0.85rem',
                    color: '#2563EB',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 1,
                  }}
                >
                  {post.category} • {post.readTime}
                </Typography>
                <Typography
                  component="h1"
                  sx={{
                    fontSize: { xs: '1.5rem', md: '2rem' },
                    fontWeight: 800,
                    color: '#1E293B',
                    lineHeight: 1.3,
                    mb: 2,
                  }}
                >
                  {post.title}
                </Typography>
                <Typography
                  sx={{
                    color: '#64748B',
                    fontSize: '0.95rem',
                    mb: 4,
                    lineHeight: 1.7,
                  }}
                >
                  {post.description}
                </Typography>
                <Box
                  sx={{
                    '& h2': {
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      color: '#1E293B',
                      mt: 3,
                      mb: 1.5,
                    },
                    '& p': { color: '#64748B', lineHeight: 1.8, mb: 2 },
                    '& ul': { pl: 2.5, mb: 2, '& li': { color: '#64748B', mb: 0.5, lineHeight: 1.7 } },
                    '& strong': { color: '#1E293B' },
                  }}
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
                <Button
                  variant="contained"
                  onClick={() => navigate(post.enrollUrl)}
                  endIcon={<ArrowRight size={18} />}
                  sx={{
                    mt: 3,
                    bgcolor: '#2563EB',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                    borderRadius: '12px',
                    '&:hover': { bgcolor: '#1D4ED8' },
                  }}
                >
                  {post.enrollButtonText}
                </Button>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pt: 10,
        pb: 8,
        bgcolor: '#F8FAFC',
      }}
    >
      <Container maxWidth="lg">
        <Box>
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              color: '#1E293B',
              mb: 1,
            }}
          >
            Blog
          </Typography>
          <Typography sx={{ color: '#64748B', lineHeight: 1.7, mb: 4 }}>
            Insights, tutorials, and updates from Reviving India
          </Typography>

          <Grid container spacing={4}>
            {BLOG_POSTS.map((post) => (
              <Grid size={{ xs: 12, md: 6, lg: 4 }} key={post.slug}>
                <Card
                    onClick={() => navigate(`/blog/${post.slug}`)}
                    sx={{
                      height: '100%',
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
                      border: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      transition: 'box-shadow 0.3s ease, transform 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 12px 32px rgba(0,0,0,0.1)',
                        transform: 'translateY(-4px)',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: 200,
                        overflow: 'hidden',
                        bgcolor: '#1e293b',
                      }}
                    >
                      <Box
                        component="img"
                        src={post.image}
                        alt={post.title}
                        loading="eager"
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'top',
                          imageRendering: 'auto',
                          transform: 'translateZ(0)',
                          backfaceVisibility: 'hidden',
                        }}
                      />
                    </Box>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography
                        component="h2"
                        sx={{
                          fontSize: '1.125rem',
                          fontWeight: 700,
                          color: '#1E293B',
                          mb: 1,
                          lineHeight: 1.35,
                        }}
                      >
                        {post.title.split('|')[0].trim()}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.9rem',
                          color: '#64748B',
                          lineHeight: 1.55,
                        }}
                      >
                        {post.description}
                      </Typography>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          color: '#2563EB',
                          fontWeight: 600,
                          fontSize: '0.875rem',
                          mt: 2,
                        }}
                      >
                        Read more
                        <ArrowRight size={16} />
                      </Box>
                    </CardContent>
                  </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default BlogPage;
